import {
  Column,
  createTableCell,
  createTableColumn,
  FormCheckbox,
  FormFieldMethods,
  FormTextField,
  FormText,
  requiredRule,
  ResponsivePaginatedTable,
} from '@gcl/react-core';
import * as React from 'react';
import { IListItemWithSelection } from '@listItem/listItem.models';
import { EDesignBriefFormKeys, EDesignBriefInfoAttachedFormKeys } from './designBriefDetailPage.models';
import './DesignBriefDetailPage.scss';
import {
  IDesignBriefInfoAttached,
  IProjectDesignBriefInfoAttached,
} from '@designBrief/infoAttached/infoAttached.models';
import { useProjectId } from '@config/config.hooks';

interface IDesignBriefDetailInfoAttachedProps {
  formFieldMethods: FormFieldMethods;
  isReadOnly: boolean;
  informationAttached: IDesignBriefInfoAttached[];
  informationAttachedSelected: IProjectDesignBriefInfoAttached[];
  className?: string;
}

export const DesignBriefDetailInfoAttached: React.FunctionComponent<IDesignBriefDetailInfoAttachedProps> = ({
  formFieldMethods,
  isReadOnly,
  informationAttachedSelected,
  informationAttached,
  className,
}) => {
  const projectId = useProjectId();

  const getKeyByIndex = {
    ID: (index: number) => `${EDesignBriefFormKeys.INFO_ATTACHED}[${index}].${EDesignBriefInfoAttachedFormKeys.ID}`,
    PROJECT_DESIGN_BRIEF_ID: (index: number) =>
      `${EDesignBriefFormKeys.INFO_ATTACHED}[${index}].${EDesignBriefInfoAttachedFormKeys.PROJECT_DESIGN_BRIEF_ID}`,
    DESIGN_BRIEF_INFO_ATTACHED_ID: (index: number) =>
      `${EDesignBriefFormKeys.INFO_ATTACHED}[${index}].${EDesignBriefInfoAttachedFormKeys.DESIGN_BRIEF_INFO_ATTACHED_ID}`,
    PROJECT_ID: (index: number) =>
      `${EDesignBriefFormKeys.INFO_ATTACHED}[${index}].${EDesignBriefInfoAttachedFormKeys.PROJECT_ID}`,
    NAME: (index: number) => `${EDesignBriefFormKeys.INFO_ATTACHED}[${index}].${EDesignBriefInfoAttachedFormKeys.NAME}`,
    PROVIDED: (index: number) =>
      `${EDesignBriefFormKeys.INFO_ATTACHED}[${index}].${EDesignBriefInfoAttachedFormKeys.PROVIDED}`,
    REMARK: (index: number) =>
      `${EDesignBriefFormKeys.INFO_ATTACHED}[${index}].${EDesignBriefInfoAttachedFormKeys.REMARK}`,
    ROWVERSION: (index: number) =>
      `${EDesignBriefFormKeys.INFO_ATTACHED}[${index}].${EDesignBriefInfoAttachedFormKeys.ROW_VERSION}`,
  };

  const columns = React.useMemo(() => {
    return [
      createTableColumn({
        Header: '',
        accessor: 'id',
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IListItemWithSelection, index: number, cellExtras: { [key: string]: any }) => {
            return <FormText isHidden formFieldMethods={formFieldMethods} id={getKeyByIndex.ROWVERSION(index)} />;
          },
        ),
      } as Column),
      createTableColumn({
        Header: '',
        accessor: 'rowVersion',
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IListItemWithSelection, index: number, cellExtras: { [key: string]: any }) => {
            return <FormText isHidden formFieldMethods={formFieldMethods} id={getKeyByIndex.ROWVERSION(index)} />;
          },
        ),
      } as Column),
      createTableColumn({
        Header: '',
        accessor: 'projectDesignBriefId',
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IListItemWithSelection, index: number, cellExtras: { [key: string]: any }) => {
            return (
              <FormText
                isHidden
                formFieldMethods={formFieldMethods}
                id={getKeyByIndex.PROJECT_DESIGN_BRIEF_ID(index)}
              />
            );
          },
        ),
      } as Column),
      createTableColumn({
        Header: '',
        accessor: 'designBriefInfoAttachedId',
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IListItemWithSelection, index: number, cellExtras: { [key: string]: any }) => {
            return (
              <FormText
                isHidden
                formFieldMethods={formFieldMethods}
                id={getKeyByIndex.DESIGN_BRIEF_INFO_ATTACHED_ID(index)}
              />
            );
          },
        ),
      } as Column),
      createTableColumn({
        Header: '',
        accessor: 'projectId',
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IListItemWithSelection, index: number, cellExtras: { [key: string]: any }) => {
            return <FormText isHidden formFieldMethods={formFieldMethods} id={getKeyByIndex.PROJECT_ID(index)} />;
          },
        ),
      } as Column),
      createTableColumn({
        Header: '',
        accessor: 'name',
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IListItemWithSelection, index: number, cellExtras: { [key: string]: any }) => {
            return <FormText formFieldMethods={formFieldMethods} id={getKeyByIndex.NAME(index)} />;
          },
        ),
      } as Column),
      createTableColumn({
        Header: 'Provided',
        accessor: 'isProvided',
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IListItemWithSelection, index: number, cellExtras: { [key: string]: any }) => {
            return (
              <FormCheckbox
                formFieldMethods={formFieldMethods}
                id={getKeyByIndex.PROVIDED(index)}
                disabled={isReadOnly}
                rules={{
                  validate: {
                    validateProvided: (v: boolean) => validateCheckbox(v, item, index) || requiredRule.required.message,
                  },
                }}
              />
            );
          },
        ),
      } as Column),
      createTableColumn({
        Header: 'Remark',
        accessor: 'remark',
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IListItemWithSelection, index: number, cellExtras: { [key: string]: any }) => {
            return (
              <FormTextField
                formFieldMethods={formFieldMethods}
                id={getKeyByIndex.REMARK(index)}
                data-testid={`remark-${index}`}
                disabled={isReadOnly}
              />
            );
          },
        ),
      } as Column),
    ];
  }, [isReadOnly]);

  const validateCheckbox: (v: boolean, item: IListItemWithSelection, index: number) => boolean = (v, item, index) => {
    if (!v) {
      return true;
    }

    const formValues = formFieldMethods.getValues();
    const remark = formValues[EDesignBriefFormKeys.INFO_ATTACHED][index];
    if (!remark || remark.length <= 0) {
      formFieldMethods.setError(
        `${EDesignBriefFormKeys.INFO_ATTACHED}[${index}].${EDesignBriefInfoAttachedFormKeys.REMARK}`,
        { message: requiredRule.required.message },
      );
    }

    return true;
  };

  const listItems: IProjectDesignBriefInfoAttached[] = React.useMemo(() => {
    const itemList = informationAttached.sort((a, b) => a.itemSeq - b.itemSeq);
    if (informationAttachedSelected?.length > 0) {
      return informationAttachedSelected.map((item, index) => {
        formFieldMethods.setValue(getKeyByIndex.NAME(index), item.designBriefInfoAttached?.itemName, {
          shouldDirty: false,
        });
        formFieldMethods.setValue(getKeyByIndex.PROJECT_ID(index), projectId, { shouldDirty: false });
        formFieldMethods.setValue(getKeyByIndex.PROJECT_DESIGN_BRIEF_ID(index), item.projectDesignBriefId, {
          shouldDirty: false,
        });
        formFieldMethods.setValue(getKeyByIndex.DESIGN_BRIEF_INFO_ATTACHED_ID(index), item.designBriefInfoAttachedId, {
          shouldDirty: false,
        });
        formFieldMethods.setValue(getKeyByIndex.PROVIDED(index), item.itemSelected, { shouldDirty: false });
        formFieldMethods.setValue(getKeyByIndex.ROWVERSION(index), item.rowVersion, { shouldDirty: false });
        formFieldMethods.setValue(getKeyByIndex.REMARK(index), item.remark, { shouldDirty: false });
        formFieldMethods.setValue(getKeyByIndex.ID(index), item.id, { shouldDirty: false });
        const { id: designBriefInfoAttachedId, ...rest } = item;
        return {
          name: item.designBriefInfoAttached?.itemName,
          ...rest,
        };
      });
    }
    return itemList.map((item, index) => {
      formFieldMethods.setValue(getKeyByIndex.NAME(index), item.itemName, { shouldDirty: false });
      formFieldMethods.setValue(getKeyByIndex.PROJECT_ID(index), projectId, { shouldDirty: false });
      formFieldMethods.setValue(getKeyByIndex.DESIGN_BRIEF_INFO_ATTACHED_ID(index), item.id, { shouldDirty: false });
      const { id: designBriefInfoAttachedId, ...rest } = item;
      return {
        name: item.itemName,
        projectId,
        designBriefInfoAttachedId,
        itemSelected: false,
        ...rest,
      };
    });
  }, [JSON.stringify(informationAttached), JSON.stringify(informationAttachedSelected)]);

  return <ResponsivePaginatedTable disablePagination allowSelectRow={false} columns={columns} items={listItems} />;
};
